<template>
  <div>
    <d-form>
      <d-form-row class="form-group">
        <d-col cols="12" md="8">
          <label>{{ $t('fences.form.name.label') }}</label>
          <d-form-input
            ref="nameInput"
            type="text"
            v-model="model.name"
            :state="hasErrorFiled('name')"
            :placeholder="$t('fences.form.name.placeholder')"
          />
          <d-form-invalid-feedback>
            {{ $t('fences.form.name.invalid') }}
          </d-form-invalid-feedback>
        </d-col>
        <d-col
          cols="12"
          md="4"
          class="d-flex align-items-end justify-content-around"
        >
          <d-checkbox
            toggle
            v-model="model.settings.showAnswer"
            class="custom-toggle-sm"
          >
            <span>{{ $t('fences.form.showAnswer.label') }}</span>
          </d-checkbox>
          <d-checkbox
            toggle
            v-model="model.settings.colored"
            class="custom-toggle-sm"
          >
            <span>{{ $t('fences.form.colored.label') }}</span>
          </d-checkbox>
        </d-col>
      </d-form-row>

      <d-form-row class="form-group align-items-start mt-3 mb-0">
        <d-col cols="12" md="2">
          <label>{{ $t('fences.form.columns.label') }}</label>
          <d-form-input
            type="number"
            min="4"
            v-model.number="model.columns"
            @input="updateTable"
            :state="isCorrectDimension"
          />
        </d-col>
        <d-col cols="12" md="2">
          <label>{{ $t('fences.form.rows.label') }}</label>
          <d-form-input
            type="number"
            min="4"
            v-model.number="model.rows"
            @input="updateTable"
            :state="isCorrectDimension"
          />
        </d-col>

        <d-col cols="12" md="4">
          <label>
            {{ $t('fences.form.extraClues.label') }}
            <i
              id="tooltip-extra-clues"
              class="fas fa-info-circle text-muted ml-1"
              style="cursor: pointer"
            />
            <d-tooltip target="tooltip-extra-clues" placement="top">
              {{ $t('fences.form.extraClues.tooltipLine1') }}
              <br />
              <br />
              {{ $t('fences.form.extraClues.tooltipLine2') }}
            </d-tooltip>
          </label>

          <d-form-input
            type="number"
            min="0"
            v-model.number="model.settings.extraClues"
          />
        </d-col>
      </d-form-row>

      <d-form-row class="form-group align-items-end mt-0">
        <d-col cols="12" md="4">
          <div v-if="isCorrectDimension === false" class="text-danger small mt-1">
            <i class="fas fa-exclamation-triangle"></i>
            {{ $t('fences.form.invalidDimension') }}
          </div>
        </d-col>
      </d-form-row>
    </d-form>

    <div class="position-relative mt-4">
      <fences-grid
        v-if="model.table.length"
        :table="model.table"
        :rows="model.rows"
        :columns="model.columns"
        :cell-size="cellSize"
        :settings="model.settings"
      />

      <fences-status-and-popover
        :base-clues-count="model.settings.baseCluesCount"
        :extra-clues="model.settings.extraClues"
        :settings="model.settings"
      />
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-webpack-loader-syntax
import FencesWorker from 'worker-loader!_utils/puzzleFences/worker.js';
import { mapActions } from 'vuex';
import mixin from '_common/mixins/ValidateForm';
import { cloneDeep } from '_utils';
import Events from '_utils/events';
import FormatErrors from '_common/mixins/FormatErrors';
import workerManager from '_common/mixins/workerManager';
import PuzzleService from '_services/rest/modules/puzzle';
import {
  _LOAD_PUZZLE_FENCES_MODEL_,
  CELL,
  CELL_SIZE,
  EDGE_COLORS,
  MODEL,
} from './PuzzleFencesModel';
import FencesGrid from './FencesGrid.vue';
import FencesStatusAndPopover from './FencesStatusAndPopover.vue';

export default {
  name: 'puzzle-fences-form',
  components: { FencesGrid, FencesStatusAndPopover },
  mixins: [mixin, FormatErrors, workerManager],
  data() {
    return {
      model: cloneDeep(MODEL),
      errors: { name: { required: false } },
      cellSize: CELL_SIZE,
      isGenerating: false,
    };
  },
  computed: {
    isCorrectDimension() {
      const { rows, columns } = this.model;

      const isOk = ((rows + 1) * (columns + 1)) % 2 === 1;
      this.setEnableExport(isOk);

      return isOk ? false : null;
    },
    totalVisibleClues() {
      if (this.model.settings.baseCluesCount === null) return null;
      return (
        this.model.settings.baseCluesCount +
        (Number(this.model.settings.extraClues) || 0)
      );
    },
  },
  methods: {
    ...mapActions(['setHasChanges', 'setPuzzleFences', 'setEnableExport']),
    resolveWorkerErrorMessage(data) {
      if (data.errorCode) {
        return this.$t(`fences.alert.workerErrors.${data.errorCode}`);
      }

      return data.message || this.$t('fences.alert.generateFailed');
    },
    initializeTable(currentTable = []) {
      const { rows, columns: cols } = this.model;
      const table = [];

      for (let y = 0; y < rows; y += 1) {
        for (let x = 0; x < cols; x += 1) {
          const existing = currentTable.find(
            c => Number(c.positionx) === x && Number(c.positiony) === y,
          );

          const baseCell = {
            ...cloneDeep(CELL),
            id: y * cols + x,
            positionx: x,
            positiony: y,
            edges: {
              top: false,
              right: false,
              bottom: false,
              left: false,
            },
            edgeColors: {
              top: null,
              right: null,
              bottom: null,
              left: null,
            },
            fixedEdges: {
              top: false,
              right: false,
              bottom: false,
              left: false,
            },
            fixedEdgeColors: {
              top: null,
              right: null,
              bottom: null,
              left: null,
            },
            extraEdges: {
              top: false,
              right: false,
              bottom: false,
              left: false,
            },
            extraEdgeColors: {
              top: null,
              right: null,
              bottom: null,
              left: null,
            },
          };

          if (existing) {
            table.push({
              ...baseCell,
              ...existing,
              edgeColors: existing.edgeColors || baseCell.edgeColors,
              fixedEdgeColors:
                existing.fixedEdgeColors || baseCell.fixedEdgeColors,
              extraEdges: existing.extraEdges || baseCell.extraEdges,
              extraEdgeColors:
                existing.extraEdgeColors || baseCell.extraEdgeColors,
            });
          } else {
            table.push(baseCell);
          }
        }
      }
      return table;
    },
    updateTable() {
      this.model.table = this.initializeTable(this.model.table);
    },
    generate() {
      const { rows, columns } = this.model;

      this.cancelPreviousGeneration();
      this.isGenerating = true;
      this.startGenerationLoader();

      const worker = new FencesWorker();
      this.workers.push(worker);

      worker.onmessage = ({ data }) => {
        if (data.status === 'finished') {
          this.$set(this.model.settings, 'rawEdges', null);
          this.$set(this.model.settings, 'rawEdges', data.edges);
          this.$set(this.model.settings, 'baseCluesCount', data.baseClues);
          this.applyEdgesToTable();
          this.activeAlert({
            message: this.$t('fences.alert.generated', {
              duration: data.duration,
            }),
            theme: 'success',
          });
        } else if (data.status === 'error') {
          this.activeAlert({
            message: this.resolveWorkerErrorMessage(data),
            theme: 'warning',
          });
        }

        this.isGenerating = false;
        this.setHasChanges(true);
        this.cleanupWorkers();
      };

      worker.onerror = err => {
        console.error('Worker Error:', err);
        this.activeAlert({
          message: this.$t('fences.alert.workerCritical'),
          theme: 'danger',
        });
        this.isGenerating = false;
        this.cleanupWorkers();
      };

      worker.postMessage({ rows, columns, extraClues: 999 });
    },
    applyEdgesToTable() {
      if (!this.model.settings.rawEdges) return;

      const { rows, columns: cols } = this.model;
      const newTable = this.initializeTable([]);
      const extraLimit = Number(this.model.settings.extraClues) || 0;

      const cellMap = {};
      newTable.forEach(c => {
        cellMap[`${c.positionx},${c.positiony}`] = c;
      });

      this.model.settings.rawEdges.forEach(edge => {
        const {
          p1, p2, isFixed, extraOrder,
        } = edge;
        const minX = Math.min(p1.x, p2.x);
        const minY = Math.min(p1.y, p2.y);

        const isBase = isFixed;
        const isExtra =
          !isBase && extraOrder !== undefined && extraOrder < extraLimit;

        const applyToCell = (x, y, dir) => {
          const cell = cellMap[`${x},${y}`];
          if (!cell) return;

          cell.edges[dir] = true;
          cell.edgeColors[dir] = EDGE_COLORS.path;

          if (isBase) {
            cell.fixedEdges[dir] = true;
            cell.fixedEdgeColors[dir] = EDGE_COLORS.fixed;
          }

          if (isExtra) {
            cell.extraEdges[dir] = true;
            cell.extraEdgeColors[dir] = EDGE_COLORS.extra;
          }
        };

        if (p1.y === p2.y) {
          if (p1.y < rows) applyToCell(minX, p1.y, 'top');
          if (p1.y > 0) applyToCell(minX, p1.y - 1, 'bottom');
        } else {
          if (p1.x < cols) applyToCell(p1.x, minY, 'left');
          if (p1.x > 0) applyToCell(p1.x - 1, minY, 'right');
        }
      });

      this.model.table = Object.freeze(newTable);
    },
    save() {
      if (!this.checkForm()) return;

      const puzzle = {
        ...this.model,
        slug: `${this.model.name.replace(/\s+/g, '-')}-fences`,
      };

      PuzzleService.save(puzzle)
        .then(response => {
          this.activeAlert({
            message: this.$t('fences.alert.gameSaved'),
            theme: 'success',
          });
          this.setPuzzleFences(response);
          if (this.$route.name === 'admin-puzzle-fences-new') {
            this.$router.push({
              name: 'admin-puzzle-fences-edit',
              params: { puzzleId: response.id },
            });
          }
          this.setHasChanges(false);
        })
        .catch(error => {
          const errors = this.formatErrors(error.data);
          this.activeAlert({ message: errors, theme: 'danger' });
        });
    },
  },
  watch: {
    'model.settings.extraClues': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal && this.model.settings.rawEdges) {
          this.applyEdgesToTable();
        }
      },
    },
  },
  mounted() {
    Events.$on(_LOAD_PUZZLE_FENCES_MODEL_, model => {
      this.model = { ...model };
    });
  },
  destroyed() {
    Events.$off(_LOAD_PUZZLE_FENCES_MODEL_);
  },
};
</script>
